import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface VisitorRegistrationResponse {
  total_unique_visitors: number;
  is_new_visitor: boolean;
}

export interface VisitorCountResponse {
  total_unique_visitors: number;
}

@Injectable({
  providedIn: 'root'
})
export class VisitorTrackingService {
  private readonly VISITOR_ID_KEY = 'visitor_id';
  private readonly API_URL = `${environment.apiUrl}/visitors`;
  
  // Observable for total visitor count
  private visitorCountSubject = new BehaviorSubject<number>(0);
  public visitorCount$ = this.visitorCountSubject.asObservable();
  
  // Flag to prevent multiple registration calls
  private registrationInProgress = false;
  private registrationCompleted = false;

  constructor(private http: HttpClient) {}

  /**
   * Initialize visitor tracking on application startup.
   * This should be called only once during app initialization.
   */
  public initializeVisitorTracking(): void {
    if (this.registrationInProgress || this.registrationCompleted) {
      return; // Prevent multiple calls
    }

    try {
      const visitorId = this.getOrCreateVisitorId();
      this.registerVisitor(visitorId);
    } catch (error) {
      console.error('Error initializing visitor tracking:', error);
      // Fallback: load visitor count without registration
      this.loadVisitorCount();
    }
  }

  /**
   * Get existing visitor ID from localStorage or generate a new UUID.
   */
  private getOrCreateVisitorId(): string {
    let visitorId = localStorage.getItem(this.VISITOR_ID_KEY);
    
    if (!visitorId || !this.isValidUUID(visitorId)) {
      visitorId = this.generateUUID();
      localStorage.setItem(this.VISITOR_ID_KEY, visitorId);
      console.log('Generated new visitor ID:', visitorId);
    } else {
      console.log('Using existing visitor ID:', visitorId);
    }
    
    return visitorId;
  }

  /**
   * Generate a UUID v4.
   */
  private generateUUID(): string {
    if (crypto.randomUUID) {
      return crypto.randomUUID();
    }
    
    // Fallback for older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Validate UUID format.
   */
  private isValidUUID(uuid: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }

  /**
   * Register visitor with backend and get total count.
   */
  private registerVisitor(visitorId: string): void {
    if (this.registrationInProgress) {
      return;
    }

    this.registrationInProgress = true;
    
    this.http.post<VisitorRegistrationResponse>(`${this.API_URL}/register`, {
      visitor_id: visitorId
    }).pipe(
      tap(response => {
        this.visitorCountSubject.next(response.total_unique_visitors);
        this.registrationCompleted = true;
        
        if (response.is_new_visitor) {
          console.log('New visitor registered. Total visitors:', response.total_unique_visitors);
        } else {
          console.log('Existing visitor updated. Total visitors:', response.total_unique_visitors);
        }
      }),
      catchError(error => {
        console.error('Error registering visitor:', error);
        // Fallback: try to get count without registration
        this.loadVisitorCount();
        return of(null);
      })
    ).subscribe({
      complete: () => {
        this.registrationInProgress = false;
      }
    });
  }

  /**
   * Load visitor count without registration (fallback method).
   */
  public loadVisitorCount(): void {
    this.http.get<VisitorCountResponse>(`${this.API_URL}/count`).pipe(
      tap(response => {
        this.visitorCountSubject.next(response.total_unique_visitors);
      }),
      catchError(error => {
        console.error('Error loading visitor count:', error);
        // Set a fallback count or keep existing
        return of({ total_unique_visitors: 0 });
      })
    ).subscribe();
  }

  /**
   * Get current visitor ID (readonly).
   */
  public getCurrentVisitorId(): string | null {
    return localStorage.getItem(this.VISITOR_ID_KEY);
  }

  /**
   * Get current visitor count (immediate value).
   */
  public getCurrentCount(): number {
    return this.visitorCountSubject.value;
  }

  /**
   * Refresh visitor count from server.
   */
  public refreshCount(): void {
    this.loadVisitorCount();
  }
}